import React from 'react';
import { Checkbox } from '@atlaskit/checkbox';
import InlineMessage from '@atlaskit/inline-message';
import { Currency, PaymentMethod, PaymentMethodType, PaymentOnTerms } from '@css-xp/graphtypes';
import { PaymentMethodId, PaymentMethodPanel } from '@atlassian/commerce-payment-methods';
import { createPaymentMethod } from '@atlassian/temp-proxies';
import { CcpPaymentMethodTypes, CreatePaymentMethodRequest } from '@atlassian/cssxp-types-billing';
import { ApolloQueryResult } from '@apollo/client';
import { errorNotificationBuilder, successNotificationBuilder, NotificationTypes } from '../../notifications';
import { useNotification } from '../../hooks';
import './additional-payment-methods.scss';
import { Divider } from '../../divider';
import { isAccess, isResourceAccess } from '../../security';
import { Permissions, Resources } from '@atlassian/cssxp-typescommon';

type Props = {
  transactionAccountId: string | undefined;
  isTermPaymentAvailable: boolean;
  deferredPaymentMethods: any[];
  defaultPaymentMethodId: string | null;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
};

const { PAYMENT_METHOD_ON_TERM_WRITE } = Permissions;

const renderDefferedPaymentMethod = (deferredPaymentMethod: PaymentMethod, defaultPaymentMethodId: string | null) => {
  if (deferredPaymentMethod.type === PaymentMethodType.DEFERRED) {
    const paymentOnTerms = deferredPaymentMethod as PaymentOnTerms;

    return (
      <PaymentMethodPanel
        key={paymentOnTerms.id}
        paymentMethod={{
          id: paymentOnTerms.id as PaymentMethodId,
          type: paymentOnTerms.type,
          default: defaultPaymentMethodId === paymentOnTerms.id,
        }}
      />
    );
  }
  return;
};

const getErrorNotificationOptions = () =>
  errorNotificationBuilder()
    .withId('add-term-payment-method-error')
    .withMessage('Error adding term payment method')
    .withDismissAfter(5000)
    .build();

const AdditionalPaymentMethodsTxa = ({
  transactionAccountId,
  isTermPaymentAvailable,
  deferredPaymentMethods,
  defaultPaymentMethodId,
  refetch,
}: Props) => {
  deferredPaymentMethods as PaymentOnTerms[];

  const { setNotificationSent, sendErrorNotification, sendSuccessNotification } = useNotification();

  const notifyError = () => {
    sendErrorNotification(getErrorNotificationOptions());
    setNotificationSent(true);
  };

  const addTermPaymentMethod = async () => {
    if (transactionAccountId) {
      try {
        const paymentMethodRequestParams: CreatePaymentMethodRequest = {
          transactionAccountId,
          data: {
            type: CcpPaymentMethodTypes.DEFERRED,
            currency: Currency.USD,
            terms: {
              invoiceDueDays: 30,
            },
            default: true,
          },
        };
        await createPaymentMethod(paymentMethodRequestParams);
        // send success notification after adding term payment method
        const notificationOptions = successNotificationBuilder()
          .withId('update-payment-method-success')
          .withHeader("You've enabled term payment method.")
          .withDismissAfter(5000)
          .isVersion(NotificationTypes.FLAG)
          .build();
        sendSuccessNotification(notificationOptions);
        refetch();
      } catch (err) {
        notifyError();
      }
    }
  };

  return (
    <div className="additional-payment-methods-container" data-testid="additional-payments">
      <p className="additional-payment-methods-header">Enable additional payment methods for this account</p>
      <div className="term-payment-container">
        <Checkbox
          value="payOnTerms"
          label="Term Payment"
          name="checkbox-pay-on-terms"
          testId="cb-pay-on-terms"
          isDisabled={
            isTermPaymentAvailable ||
            !(isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) && isAccess(PAYMENT_METHOD_ON_TERM_WRITE))
          }
          isChecked={isTermPaymentAvailable}
          onChange={addTermPaymentMethod}
        />
        <div className="term-payment-details">
          <div className="warning-message-container">
            <InlineMessage type="warning" secondaryText="Term payment can't be disabled once it's set." />
          </div>
          <div className="term-payments">
            {deferredPaymentMethods &&
              deferredPaymentMethods.length > 0 &&
              deferredPaymentMethods.map((paymentMethod) => {
                return renderDefferedPaymentMethod(paymentMethod, defaultPaymentMethodId);
              })}
          </div>
        </div>
      </div>
      <Divider />
      <Checkbox
        value="payOnAccount"
        label="Pay on account"
        name="checkbox-pay-on-account"
        isDisabled
        testId="cb-pay-on-account"
      />
    </div>
  );
};

export default AdditionalPaymentMethodsTxa;
